/* eslint-disable no-template-curly-in-string */
import config from "@Config/index";
import { AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { isDemoWhitelabelSubdomain } from "constants/index";
import checkValidWhitelabelSubdomain from "helpers/checkValidWhitelabelSubdomain";
import { processColors } from "helpers/colorHelper";
import { getCountryWiseData } from "helpers/getCountryWiseData";
import getSubdomain from "helpers/getSubdomain";
import { processFonts } from "helpers/fontHelper";

interface CustomLink {
  title: string;
  link: string;
}

export type AnnouncementBanner = {
  title: string;
  link: string;
};

interface Footer {
  bg_color: string;
  logo: string;
  is_link_enabled: boolean;
  links: CustomLink[];
}

interface Listing {
  cta_color: string;
  listing_preferences: string;
  distance: string;
}

interface PartnerDomain {
  title: string;
  url: string;
  fav_icon: string;
}

interface MultiRegionText {
  title: string;
  sub_title: string;
}

export type UniversitiesList = {
  id: number;
  name: string;
  canonical_name: string;
  location_country_long_name: string;
  images: Array<{
    path: string;
    caption: string;
    type: string;
  }>;
  redirectionLink?: string;
};

export type CitiesList = {
  id: number;
  name: string;
  canonical_name: string;
  location_country_long_name: string;
  images: Array<{
    path: string;
    caption: string;
    type: string;
  }>;
  campuses: Array<{
    id: number;
    name: string;
    canonical_name: string;
    parent_id: number;
    parent_name: string;
    parent_canonical_name: string;
  }>;
  redirectionLink?: string;
};

export type Colors = {
  brand_color_0: string;
  brand_color_1: string;
  brand_color_2: string;
  brand_color_3: string;
  brand_color: string;
  cta_color: string;
  footer_bg_color: string;
  primary_hover_color: string;
  primary_text_color: string;
  primary_text_hover_color: string;
  cta_text_color: string;
  cta_hover_color: string;
  button_hover_color: string;
  footer_text_color: string;
  footer_text_hover_color: string;
  accessible_brand_color: string;
  active_background_color: string;
};

export type FontPreference = {
  mode: "single" | "pairing";
  single_font?: string;
  title_font?: string;
  body_font?: string;
};

export type Themes = {
  isBrandThemeLight?: boolean;
  isCtaThemeLight?: boolean;
  isFooterThemeLight?: boolean;
};

interface WhitelableConfig {
  brand_color: string;
  logo: string;
  redirect_url: string;
  hero_text: string;
  sub_text: string;
  hero_banner: string;
  page_title: string;
  is_custom_link_enabled: boolean;
  custom_links: CustomLink[];
  is_announcement_banner_enabled: boolean;
  announcement_banners: AnnouncementBanner[];
  is_feature_listing_1_enabled: boolean;
  is_feature_listing_2_enabled: boolean;
  is_multi_region_enabled: boolean;
  is_university_properties_section_enabled: boolean;
  footer: Footer;
  listing: Listing;
  domain: PartnerDomain;
  multi_region_text: MultiRegionText;
  is_global_search_enabled: boolean;
  is_referral_card_enabled: boolean;
  font_preference: FontPreference;
}

interface RequestDetails {
  hostname: string;
  port: string;
}

interface FormField {
  id: number;
  field_type: "text" | "number" | "country_selection" | "dropdown" | "textarea" | string;
  default_validation: Record<string, any>;
  default_data: any[];
}

interface PartnerCustomFormFieldConfig {
  id: number;
  form_id: number;
  form_field_id: number;
  label: string;
  data: Record<string, any>;
  is_required: boolean;
  placeholder_text: string | null;
  status: "active" | "paused" | "deleted" | string;
  form_field: FormField;
}

interface WhitelabelData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  error: string | null;
  whitelabel_region_canonical_name: string;
  region_canonical_names: Array<any>;
  isWhitelabel: boolean;
  isWhitelabelDataFetched: boolean | null;
  partner_id: string;
  partner_uuid: string;
  partner_name: string;
  subdomain: string;
  whitelabel_domain_url: string;
  university_mock: boolean;
  config: WhitelableConfig | null;
  colors: Colors | null;
  requestDetails: RequestDetails;
  isUniversityPartner: boolean;
  partnerType: string;
  universities_list: UniversitiesList[];
  cities_list: CitiesList[];
  countryWiseCityData: Record<string, CitiesList[]>;
  countryWiseUniversityData: Record<string, UniversitiesList[]>;
  themes: Themes;
  fonts: FontPreference | null;
  font_urls: string[];
  isPartnerCustomFormEnabled: boolean;
  isCustomFormForAllInventories: boolean;
  partnerCustomFormFieldConfig: PartnerCustomFormFieldConfig[];
  isUSAPartner: boolean;
  isWhitelabelSeoEnabled: boolean;
  whitelabelGoogleSiteVerificationId: string;
  isPoweredByAmberEnabledForWhitelabelFooter: boolean;
  agentId: number | null;
  isSubPartnerRedirectToWhitelabel: boolean;
  config_translations: Record<string, Partial<WhitelableConfig>> | null;
}

export const initialState: WhitelabelData = {
  whitelabel_region_canonical_name: "",
  region_canonical_names: [],
  readyStatus: "invalid",
  error: null,
  isWhitelabel: false,
  isWhitelabelDataFetched: null,
  partner_id: "",
  partner_uuid: "",
  partner_name: "",
  subdomain: "",
  whitelabel_domain_url: "",
  university_mock: false,
  config: null, // Initial state with no config data
  colors: null,
  requestDetails: {
    hostname: "",
    port: "",
  },
  isUniversityPartner: false,
  partnerType: "",
  universities_list: [],
  cities_list: [],
  countryWiseCityData: {},
  countryWiseUniversityData: {},
  themes: {},
  fonts: null,
  font_urls: [],
  isPartnerCustomFormEnabled: false,
  isCustomFormForAllInventories: false,
  partnerCustomFormFieldConfig: [],
  isUSAPartner: false,
  isWhitelabelSeoEnabled: false,
  whitelabelGoogleSiteVerificationId: "",
  isPoweredByAmberEnabledForWhitelabelFooter: false,
  agentId: null,
  isSubPartnerRedirectToWhitelabel: false,
  config_translations: null,
};

const whitelabelData = createSlice({
  name: "whitelabelData",
  initialState,
  reducers: {
    updateWhitelabelDataRequesting: (state) => {
      state.readyStatus = "request";

      if (__CLIENT__) {
        window.scrollTo(0, 0);
      }
    },
    getWhitelabelDataSuccess: (state, { payload }: PayloadAction<any>) => {
      state.readyStatus = "success";
      state.config = payload?.config;
      state.whitelabel_region_canonical_name = payload?.data?.default_region_canonical_name;
      state.region_canonical_names = payload?.data?.multiple_region_canonical_name;
      state.partner_id = payload?.id;
      state.partner_name = payload?.name;
      state.isWhitelabelDataFetched = true;
      state.partner_uuid = payload?.uuid;
      state.isUniversityPartner =
        payload?.data?.type === "sub_partner"
          ? payload?.data?.partner_type.toLowerCase() === "university"
          : payload?.data?.type.toLowerCase() === "university";
      state.partnerType =
        payload?.data?.type === "sub_partner"
          ? payload?.data?.partner_type.toLowerCase()
          : payload?.data?.type.toLowerCase();
      state.universities_list = payload?.whitelabel_universities_list || [];
      state.cities_list = payload?.whitelabel_cities_list || [];
      state.countryWiseCityData = getCountryWiseData(payload?.whitelabel_cities_list);
      state.countryWiseUniversityData = getCountryWiseData(payload?.whitelabel_universities_list);
      state.isPartnerCustomFormEnabled = payload?.is_partner_custom_form_enabled;
      state.isCustomFormForAllInventories = payload?.is_custom_form_for_all_inventories;
      state.isUSAPartner = payload?.default_region_country_name == "United States";
      state.isWhitelabelSeoEnabled = payload?.data?.whitelabel_seo_enabled || false;
      state.whitelabelGoogleSiteVerificationId =
        payload?.data?.whitelabel_google_site_verification_id || "";
      state.isPoweredByAmberEnabledForWhitelabelFooter =
        payload?.is_powered_by_amber_enabled_for_whitelabel_footer || false;
      state.agentId = payload?.agent_id || null;
      state.isSubPartnerRedirectToWhitelabel =
        payload?.enable_subpartner_redirect_for_whitelabel || false;
      state.config_translations = payload?.config_translations || null;
    },
    getWhitelabelColorsSuccess: (state, { payload }: PayloadAction<any>) => {
      state.colors = payload.colors;
      state.themes = payload.themes;
    },
    getWhitelabelDataFailure: (state, { payload }: PayloadAction<any>) => {
      state.readyStatus = "failure";
      state.error = JSON.stringify(payload);
      state.isWhitelabelDataFetched = false;
    },
    geWhitleLabelPageSuccess: (state, { payload }: PayloadAction<any>) => {
      state.isWhitelabel = true;
      state.subdomain = payload?.subdomain;
      state.partner_uuid = payload?.partner_id;
      state.university_mock = payload?.university_mock;
      state.requestDetails.hostname = payload?.hostname;
      state.requestDetails.port = payload?.port;
      state.whitelabel_domain_url = payload?.domainUrl;
    },
    geWhitleLabelPageFailure: (state) => {
      state.isWhitelabel = false;
    },
    getWhitelabelFontsSuccess: (
      state,
      { payload }: PayloadAction<{ fonts: FontPreference; font_urls: string[] }>,
    ) => {
      state.fonts = payload.fonts;
      state.font_urls = payload.font_urls || [];
    },
    getPartnerCustomFormFieldConfig: (state, { payload }: PayloadAction<any>) => {
      if (payload) {
        state.partnerCustomFormFieldConfig = payload;
      }
    },
  },
});

export default whitelabelData.reducer;
export const {
  updateWhitelabelDataRequesting,
  getWhitelabelDataSuccess,
  getWhitelabelColorsSuccess,
  getWhitelabelDataFailure,
  geWhitleLabelPageSuccess,
  geWhitleLabelPageFailure,
  getWhitelabelFontsSuccess,
  getPartnerCustomFormFieldConfig,
} = whitelabelData.actions;

export const whitelabelMiddlewareServer =
  (props: any): AppThunk =>
  async (dispatch) => {
    const { hostname, subdomain, port, domainUrl } = getSubdomain(props.req);
    const params = {
      methods: "whitelabel_cities_list,whitelabel_universities_list,default_region_country_name",
    };
    if (!config.IS_WHITELABEL_APP || !subdomain) {
      dispatch(geWhitleLabelPageFailure());
    } else if (isDemoWhitelabelSubdomain(subdomain)) {
      const { partner_id, universityMock } = props.req.query;
      try {
        dispatch(
          geWhitleLabelPageSuccess({
            subdomain,
            partner_id,
            ...(universityMock === "true" && { university_mock: true }),
            hostname,
            port,
            domainUrl,
          }),
        );
        dispatch(updateWhitelabelDataRequesting());
        const apiUrl =
          config.WEB_SERVICE_HOST === "localhost"
            ? `${config.API_URL}/api/v0/leads/partners/${partner_id}`
            : `${config.API_URL}/api/v0/leads/partners/configuration?partner_id=${partner_id}`;

        const [partnerWhitelabelConfigResult, partnerCustomFormResult] = await Promise.allSettled([
          axios(apiUrl, { params }),
          axios(
            `${config.API_URL}/api/v0/partner_custom_form_field_configs?partner_id=${partner_id}`,
          ),
        ]);

        if (partnerWhitelabelConfigResult.status === "fulfilled") {
          const res = partnerWhitelabelConfigResult.value;
          dispatch(getWhitelabelDataSuccess(res.data.data));
          dispatch(getWhitelabelColorsSuccess(processColors(res.data.data)));
          dispatch(getWhitelabelFontsSuccess(processFonts(res.data.data)));
        }

        if (partnerCustomFormResult.status === "fulfilled") {
          const partnerCustomFormRes = partnerCustomFormResult.value;
          dispatch(getPartnerCustomFormFieldConfig(partnerCustomFormRes.data.data));
        }
      } catch (error) {
        dispatch(getWhitelabelDataFailure(error));
      }
    } else if (checkValidWhitelabelSubdomain(subdomain)) {
      try {
        dispatch(updateWhitelabelDataRequesting());
        const [partnerWhitelabelConfigResult, partnerCustomFormResult] = await Promise.allSettled([
          axios(`${config.API_URL}/api/v0/leads/partners/whitelabel_domain_url`, {
            params: {
              whitelabel_domain_url: domainUrl,
              ...params,
            },
          }),
          axios(
            `${config.API_URL}/api/v0/partner_custom_form_field_configs?partner_id=${domainUrl}`,
          ),
        ]);

        if (partnerWhitelabelConfigResult.status === "fulfilled") {
          const res = partnerWhitelabelConfigResult.value;
          dispatch(getWhitelabelDataSuccess(res.data.data));
          dispatch(getWhitelabelColorsSuccess(processColors(res.data.data)));
          dispatch(getWhitelabelFontsSuccess(processFonts(res.data.data)));
        }

        if (partnerCustomFormResult.status === "fulfilled") {
          const partnerCustomFormRes = partnerCustomFormResult.value;
          dispatch(getPartnerCustomFormFieldConfig(partnerCustomFormRes.data.data));
        }
      } catch (error) {
        dispatch(getWhitelabelDataFailure(error));
      }
    }
  };

// only for client
export const whitelabelMiddlewareClient =
  (
    subdomain: string,
    partner_id: string,
    hostname?: string,
    port?: string,
    domainUrl?: string,
  ): AppThunk =>
  async (dispatch) => {
    const params = {
      methods: "whitelabel_cities_list,whitelabel_universities_list,default_region_country_name",
    };
    try {
      dispatch(geWhitleLabelPageSuccess({ subdomain, partner_id, hostname, port, domainUrl }));
      if (isDemoWhitelabelSubdomain(subdomain)) {
        dispatch(updateWhitelabelDataRequesting());
        const apiUrl =
          config.WEB_SERVICE_HOST === "localhost"
            ? `${config.API_URL}/api/v0/leads/partners/${partner_id}`
            : `${config.API_URL}/api/v0/leads/partners/configuration?partner_id=${partner_id}`;
        const [partnerWhitelabelConfigResult, partnerCustomFormResult] = await Promise.allSettled([
          axios(apiUrl, { params }),
          axios(
            `${config.API_URL}/api/v0/partner_custom_form_field_configs?partner_id=${partner_id}`,
          ),
        ]);

        if (partnerWhitelabelConfigResult.status === "fulfilled") {
          const res = partnerWhitelabelConfigResult.value;
          dispatch(getWhitelabelDataSuccess(res.data.data));
          dispatch(getWhitelabelColorsSuccess(processColors(res.data.data)));
          dispatch(getWhitelabelFontsSuccess(processFonts(res.data.data)));
        }

        if (partnerCustomFormResult.status === "fulfilled") {
          const partnerCustomFormRes = partnerCustomFormResult.value;
          dispatch(getPartnerCustomFormFieldConfig(partnerCustomFormRes.data.data));
        }
      } else {
        dispatch(updateWhitelabelDataRequesting());
        const [partnerWhitelabelConfigResult, partnerCustomFormResult] = await Promise.allSettled([
          axios(`${config.API_URL}/api/v0/leads/partners/whitelabel_domain_url`, {
            params: {
              whitelabel_domain_url: domainUrl,
              ...params,
            },
          }),
          axios(
            `${config.API_URL}/api/v0/partner_custom_form_field_configs?partner_id=${domainUrl}`,
          ),
        ]);

        if (partnerWhitelabelConfigResult.status === "fulfilled") {
          const res = partnerWhitelabelConfigResult.value;
          dispatch(getWhitelabelDataSuccess(res.data.data));
          dispatch(getWhitelabelColorsSuccess(processColors(res.data.data)));
          dispatch(getWhitelabelFontsSuccess(processFonts(res.data.data)));
        }

        if (partnerCustomFormResult.status === "fulfilled") {
          const partnerCustomFormRes = partnerCustomFormResult.value;
          dispatch(getPartnerCustomFormFieldConfig(partnerCustomFormRes.data.data));
        }
      }
    } catch (error) {
      dispatch(getWhitelabelDataFailure(error));
    }
  };
